// src/hooks/useInstructorCopilot.js
//
// State machine hook for the instructor copilot drafting workflow.
// Manages the full lifecycle:
//   idle → generating → streaming → review → applying → done
//
// Pass 35 additions:
//   - 'streaming' phase: draftText accumulates live via onToken callback
//   - Request latch: prevents double-fire via activeRequestRef
//   - Simulated streaming: when backend doesn't stream, simulates word-by-word display
//
// The hook is pure of side effects beyond AI calls and applyFn.

import { useState, useRef, useCallback, useMemo } from 'react';
import { getDraftTypeConfig } from '../utils/copilotDraftTypes';
import { buildInstructorContextClause } from '../utils/instructorCopilot';
import { buildInstructorProfileClause } from '../utils/instructorAiProfile';

/**
 * Compute edit distance ratio between two strings.
 * Returns a 0–1 ratio where 0 = identical, 1 = completely different.
 */
function computeEditDistanceRatio(original, edited) {
  if (!original && !edited) return 0;
  if (!original) return 1;
  if (!edited) return 1;
  if (original === edited) return 0;

  const s1 = original.slice(0, 2000);
  const s2 = edited.slice(0, 2000);
  const m = s1.length;
  const n = s2.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        s1[i - 1] === s2[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }

  const dist = dp[m][n];
  const maxLen = Math.max(m, n);
  return maxLen === 0 ? 0 : dist / maxLen;
}

/**
 * Simulate streaming by emitting words one at a time from a full text.
 * Returns a cleanup function to cancel the simulation.
 */
function simulateStreaming(fullText, onToken, onDone, delayMs = 20) {
  const words = fullText.split(/(\s+)/); // keep whitespace
  let index = 0;
  let cancelled = false;

  function emitNext() {
    if (cancelled || index >= words.length) {
      if (!cancelled) onDone();
      return;
    }
    onToken(words[index]);
    index++;
    setTimeout(emitNext, delayMs);
  }

  setTimeout(emitNext, delayMs);

  return () => { cancelled = true; };
}

/**
 * @param {object} params
 * @param {string} params.draftType — key into DRAFT_TYPES
 * @param {object} params.signals  — pre-built client signals object
 * @param {function} [params.applyFn] — async (payload) => void
 * @param {object|null} [params.instructorProfile]
 * @param {function} [params.onEditDistance] — (ratio: number) => void
 */
export function useInstructorCopilot({ draftType, signals, applyFn, instructorProfile, onEditDistance }) {
  const config = useMemo(() => getDraftTypeConfig(draftType), [draftType]);

  // ── State machine ─────────────────────────────────────────
  const [phase, setPhase] = useState('idle');
  // 'idle' | 'generating' | 'streaming' | 'review' | 'applying' | 'done'

  // ── Draft content ─────────────────────────────────────────
  const [draftText, setDraftText] = useState('');
  const [originalAiDraft, setOriginalAiDraft] = useState('');
  const [editableFields, setEditableFields] = useState(null);
  const [aiResult, setAiResult] = useState(null);

  // ── Error state ───────────────────────────────────────────
  const [generateError, setGenerateError] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // ── Last quickInput (for regeneration) ───────────────────
  const lastQuickInputRef = useRef(null);
  const lastContextClauseRef = useRef('');

  // ── Request latch — prevents double-fire ──────────────────
  const activeRequestRef = useRef(null);
  const cancelStreamRef = useRef(null);

  // ── Core: generate ────────────────────────────────────────
  const runGenerate = useCallback(
    async (quickInput, contextClause) => {
      // Latch: if a request is already in flight, reject
      if (activeRequestRef.current) return;

      const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      activeRequestRef.current = requestId;

      lastQuickInputRef.current = quickInput;
      lastContextClauseRef.current = contextClause || '';
      setGenerateError(null);
      setPhase('generating');

      // Cancel any previous stream simulation
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }

      try {
        const profileClause = buildInstructorProfileClause(instructorProfile || null);
        const raw = await config.callFn(signals, contextClause, quickInput, profileClause);

        // Stale check: if another request replaced us, discard
        if (activeRequestRef.current !== requestId) return;

        const normalized = config.normalizeResult(raw);
        const draft = config.resultToDraft ? config.resultToDraft(normalized) : '';
        const fields = config.editableFieldsFromResult
          ? config.editableFieldsFromResult(normalized)
          : null;

        // For text/fields reviewModes: simulate streaming
        // For custom reviewMode: skip streaming (structured data, not text)
        if (config.reviewMode !== 'custom' && draft.length > 0) {
          setAiResult(normalized);
          setEditableFields(fields);
          setPhase('streaming');

          let accumulated = '';
          cancelStreamRef.current = simulateStreaming(
            draft,
            (token) => {
              if (activeRequestRef.current !== requestId) return;
              accumulated += token;
              setDraftText(accumulated);
            },
            () => {
              if (activeRequestRef.current !== requestId) return;
              setDraftText(draft);
              setOriginalAiDraft(draft);
              setPhase('review');
              activeRequestRef.current = null;
            }
          );
        } else {
          // Custom review mode or empty draft — skip streaming
          setAiResult(normalized);
          setDraftText(draft);
          setOriginalAiDraft(draft);
          setEditableFields(fields);
          setPhase('review');
          activeRequestRef.current = null;
        }
      } catch (err) {
        if (activeRequestRef.current !== requestId) return;
        setGenerateError(err?.message || 'Could not generate a draft.');
        setPhase('idle');
        activeRequestRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, signals, instructorProfile]
  );

  // ── Handlers ──────────────────────────────────────────────

  const handleGenerate = useCallback(
    (quickInput, contextClause) => {
      if (phase === 'generating' || phase === 'streaming' || phase === 'applying') return;
      runGenerate(quickInput, contextClause);
    },
    [phase, runGenerate]
  );

  const handleRegenerate = useCallback(() => {
    if (phase === 'generating' || phase === 'streaming' || phase === 'applying') return;
    // Clear latch so regenerate works
    activeRequestRef.current = null;
    if (cancelStreamRef.current) { cancelStreamRef.current(); cancelStreamRef.current = null; }
    const qi = lastQuickInputRef.current;
    if (!qi) return;
    runGenerate(qi, buildInstructorContextClause(qi));
  }, [phase, runGenerate]);

  const handleDraftChange = useCallback((text) => {
    setDraftText(text);
  }, []);

  const handleFieldChange = useCallback((key, value) => {
    setEditableFields((current) =>
      current ? current.map((f) => (f.key === key ? { ...f, value } : f)) : current
    );
  }, []);

  const handleAccept = useCallback(async () => {
    if (phase !== 'review') return;
    setApplyError(null);

    if (onEditDistance && config.reviewMode !== 'custom') {
      const ratio = computeEditDistanceRatio(originalAiDraft, draftText);
      onEditDistance(ratio);
    }

    setPhase('applying');
    try {
      let payload;
      if (config.reviewMode === 'fields') {
        const fieldsMap = {};
        (editableFields || []).forEach((f) => {
          fieldsMap[f.key] = f.value;
        });
        payload = { draftText, editableFields: fieldsMap };
      } else if (config.reviewMode === 'custom') {
        payload = aiResult;
      } else {
        payload = draftText;
      }
      await applyFn?.(payload);
      setPhase('done');
    } catch (err) {
      setApplyError(err?.message || 'Could not apply the draft.');
      setPhase('review');
    }
  }, [phase, config.reviewMode, draftText, originalAiDraft, editableFields, aiResult, applyFn, onEditDistance]);

  const handleDiscard = useCallback(() => {
    // Cancel active stream
    if (cancelStreamRef.current) { cancelStreamRef.current(); cancelStreamRef.current = null; }
    activeRequestRef.current = null;

    setPhase('idle');
    setDraftText('');
    setOriginalAiDraft('');
    setEditableFields(null);
    setAiResult(null);
    setGenerateError(null);
    setApplyError(null);
    lastQuickInputRef.current = null;
    lastContextClauseRef.current = '';
  }, []);

  const reset = handleDiscard;

  return {
    // State machine
    phase,
    generating: phase === 'generating' || phase === 'streaming',
    streaming: phase === 'streaming',
    applying: phase === 'applying',

    // Draft content
    draftText,
    originalAiDraft,
    editableFields,
    aiResult,

    // Errors
    generateError,
    applyError,

    // Handlers
    handleGenerate,
    handleRegenerate,
    handleDraftChange,
    handleFieldChange,
    handleAccept,
    handleDiscard,
    reset,
  };
}
